import React, { useMemo, useState, memo, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import DataList from '../../components/DataList';
import { getTimelineTableConfig, PAGE_CONFIG } from './config';
import EmptyTableIndicator from '../../components/EmptyTableIndicator';
import { faFileContract } from '@fortawesome/free-solid-svg-icons';
import ActionToolbar from '../../components/ActionToolbar';
import AddButton from '../../components/AddButton';
import TimelineModal from './TimelineModal';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../context/ThemeProvider';

// Import custom hooks
import { useModals } from '../../hooks/useModals';
import {
    useCreateTimeline,
    useUpdateTimeline,
    useDeleteTimeline,
    useTimelineData,
    useTimelineForm,
    useTimelineActions,
    useTimelineMediaTypes
} from './hooks/index';


const Timeline = () => {

    const { t } = useTranslation();
    const tr = useCallback((key) => t(`timelineAdmin:${key}`), [t]);
    const { theme } = useTheme();
    const queryClient = useQueryClient();

    // Modal management hook
    const {
        isOpen,
        isDeleteOpen,
        selectedItem,
        handleOpenModal,
        handleCloseModal,
        handleOpenDeleteModal,
        handleCloseDeleteModal
    } = useModals();

    // CRUD hooks
    const { createTimeline, createStatus } = useCreateTimeline(queryClient, theme);
    const { updateTimeline, updateStatus } = useUpdateTimeline(queryClient, theme);
    const { deleteTimeline, isDeleting } = useDeleteTimeline(queryClient, theme, handleCloseDeleteModal);

    //     Data management hook
    const {
        data,
        pagination,
        isLoading,
        searchQuery,
        handlePageChange,
        handlePageSizeChange,
        handleSearch
    } = useTimelineData();

    // Timeline media types hook
    const { timelineMediaTypes, timelineMediaTypesError } = useTimelineMediaTypes();

    // Form management hook
    const {
        form,
        files,
        errors,
        isEdit,
        selectedItem: formSelectedItem,
        handleInputChange,
        handleFileChange,
        resetForm,
        setEditData,
        setSelectedItem,
        handleSubmit
    } = useTimelineForm(createTimeline, updateTimeline, handleCloseModal, timelineMediaTypes);

    // Actions hook
    const {
        downloadingId,
        handleEdit,
        handleDownload,
        truncateDescription: truncate
    } = useTimelineActions(theme, handleOpenDeleteModal, setEditData);

    // Handles the open and close of the modal
    const handleOpenCreateModal = useCallback(() => {
        handleOpenModal();
        resetForm();
    }, [handleOpenModal, resetForm]);

    const handleCloseModalWithReset = useCallback(() => {
        handleCloseModal();
        resetForm();
    }, [handleCloseModal, resetForm]);

    const handleEditWithModal = useCallback((item) => {
        handleOpenModal();
        handleEdit(item);
    }, [handleOpenModal, handleEdit]);

    const handleDeleteWithModal = useCallback((item) => {
        handleOpenDeleteModal();
        setSelectedItem(item);
    }, [handleOpenDeleteModal, setSelectedItem]);

    const columns = useMemo(() => getTimelineTableConfig(handleEditWithModal, handleDeleteWithModal), [handleEditWithModal, handleDeleteWithModal]);

    return (
        <div className="flex">
            <div className="flex-1">
                <PageHeader items={PAGE_CONFIG.BREADCRUMB} pageTitle={PAGE_CONFIG.PAGE_TITLE} />
                <ActionToolbar

                    searchQuery={searchQuery}
                    onSearchChange={handleSearch}
                    actionButton={<AddButton
                        isLoading={false}
                        onClick={handleOpenCreateModal}
                        label={'Add Timeline'}
                    />} disabled={false} />
                {data?.length > 0 ? (
                    <DataList data={data} columns={columns} pagination={pagination} />
                ) : (
                    <EmptyTableIndicator colSpan={5} isLoading={isLoading} icon={faFileContract} description={PAGE_CONFIG.SEARCH_DESCRIPTION} placeholder={PAGE_CONFIG.SEARCH_PLACEHOLDER} loadingMessage="Loading..." />
                )}
                <TimelineModal
                    handleChange={handleInputChange}
                    handleSubmit={handleSubmit}
                    handleFileChange={handleFileChange}
                    form={form}
                    isOpen={isOpen}
                    onClose={handleCloseModalWithReset}
                    selectedItem={formSelectedItem}
                    setIsEdit={setEditData}
                    errors={errors}
                    onSubmit={handleSubmit}
                    isLoading={createStatus === 'pending' || updateStatus === 'pending'}
                    status={createStatus}
                    isEdit={isEdit}
                    timelineMediaTypes={timelineMediaTypes}
                />
                <ConfirmationDialog
                    isLoading={isDeleting}
                    title={'Delete Timeline'}
                    onConfirm={() => deleteTimeline(formSelectedItem?.id)}
                    type="DELETE"
                    message={`Are you sure you want to delete this timeline? This action cannot be undone.`}
                    isOpen={isDeleteOpen}
                    onClose={handleCloseDeleteModal}
                />
            </div>
        </div>
    );
};

export default memo(Timeline);
